import React, { ReactNode, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './IOSFormGroup.css';

interface IOSFormGroupProps {
  children: ReactNode;
  className?: string;
}

/**
 * iOS风格的表单分组组件
 * 用于将相关的表单项分组显示
 */
export const IOSFormGroup: React.FC<IOSFormGroupProps> = ({ children, className = '' }) => {
  return (
    <div className={`bg-white dark:bg-ios-dark-gray5 rounded-ios-lg mx-4 mb-2 divide-y divide-ios-gray5 dark:divide-ios-dark-gray4 ${className}`}>
      {children}
    </div>
  );
};

interface IOSFormItemProps {
  children: ReactNode;
  className?: string;
}

/**
 * iOS风格的表单项组件
 * 用于在表单分组中显示单个表单项
 */
export const IOSFormItem: React.FC<IOSFormItemProps> = ({ children, className = '' }) => {
  return (
    <div className={`p-4 ${className}`}>
      {children}
    </div>
  );
};

interface IOSFormLabelProps {
  children: ReactNode;
  className?: string;
}

/**
 * iOS风格的表单标签组件
 */
export const IOSFormLabel: React.FC<IOSFormLabelProps> = ({ children, className = '' }) => {
  return (
    <span className={`text-ios-body ${className} min-w-24`}>
      {children}
    </span>
  );
};

interface IOSFormToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的开关组件
 */
export const IOSFormToggle: React.FC<IOSFormToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  className = ''
}) => {
  return (
    <div className={`relative inline-block w-10 align-middle select-none ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 border-ios-gray5 appearance-none cursor-pointer"
      />
      <label
        className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${
          checked ? 'bg-ios-green dark:bg-ios-dark-green' : 'bg-ios-gray5 dark:bg-ios-dark-gray4'
        } ${disabled ? 'opacity-50' : ''}`}
      ></label>
    </div>
  );
};

interface IOSFormInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'tel' | 'url';
  disabled?: boolean;
  className?: string;
  showPasswordToggle?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
}

/**
 * iOS风格的输入框组件
 */
export const IOSFormInput = React.forwardRef<HTMLInputElement, IOSFormInputProps>(({
  value,
  onChange,
  placeholder = '',
  type = 'text',
  disabled = false,
  className = '',
  showPasswordToggle = false,
  onBlur,
  onFocus,
  autoFocus = false
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);

  // 如果是密码类型且启用了切换功能，则根据状态决定实际类型
  const actualType = type === 'password' && showPasswordToggle
    ? (showPassword ? 'text' : 'password')
    : type;

  // 如果是密码类型且启用了切换功能，需要包装在相对定位的容器中
  if (type === 'password' && showPasswordToggle) {
    return (
      <div className="relative">
        <input
          ref={ref}
          type={actualType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onFocus={onFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`w-full text-ios-body bg-transparent border-none focus:outline-none focus:ring-0 placeholder-ios-gray3 dark:placeholder-ios-dark-gray3 pr-10 ${className} ${disabled ? 'opacity-50' : ''}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2 text-ios-gray dark:text-ios-dark-gray hover:text-ios-blue dark:hover:text-ios-blue transition-colors"
          disabled={disabled}
        >
          {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  return (
    <input
      ref={ref}
      type={actualType}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onFocus={onFocus}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      className={`w-full text-ios-body bg-transparent border-none focus:outline-none focus:ring-0 placeholder-ios-gray3 dark:placeholder-ios-dark-gray3 ${className} ${disabled ? 'opacity-50' : ''}`}
    />
  );
});

interface IOSFormTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  className?: string;
  maxHeightMode?: 'fixed' | 'viewport'; // 新增：最大高度模式
  fixedMaxHeight?: number; // 新增：固定最大高度（像素）
}

/**
 * iOS风格的多行文本输入框组件
 */
export const IOSFormTextarea: React.FC<IOSFormTextareaProps> = ({
  value,
  onChange,
  placeholder = '',
  rows = 4,
  disabled = false,
  className = '',
  maxHeightMode = 'fixed',
  fixedMaxHeight = 200
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // 计算最大高度
  const getMaxHeight = React.useCallback(() => {
    if (maxHeightMode === 'viewport') {
      // 基于视口高度动态计算
      // 100vh - 顶部导航(3rem) - 底部TabBar(4rem) - 模态框标题栏(3rem) - 表单其他元素预留空间(8rem) - 安全区域
      // 约等于 100vh - 18rem = 82vh
      const viewportHeight = window.innerHeight;
      const reservedSpace = 28 * 16; // 18rem in pixels (1rem = 16px)
      return Math.max(120, viewportHeight - reservedSpace); // 最小120px
    } else {
      // 使用固定像素值
      return fixedMaxHeight;
    }
  }, [maxHeightMode, fixedMaxHeight]);

  // 自动调整高度
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 重置高度以获取正确的 scrollHeight
      textarea.style.height = 'auto';
      // 设置新高度，但不超过最大高度
      const maxHeight = getMaxHeight();
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, getMaxHeight]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      className={`w-full bg-transparent border-none focus:outline-none focus:ring-0 resize-none placeholder-ios-gray3 dark:placeholder-ios-dark-gray3 text-ios-body overflow-y-auto ${className} ${disabled ? 'opacity-50' : ''}`}
    />
  );
};

interface IOSFormSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的选择器组件
 */
export const IOSFormSelect: React.FC<IOSFormSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = '请选择',
  disabled = false,
  className = ''
}) => {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`w-full bg-transparent border-none focus:outline-none focus:ring-0 text-ios-body ${disabled ? 'opacity-50' : ''} ${className}`}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </option>
      ))}
    </select>
  );
};

interface IOSFormDateTimeProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的日期时间选择器组件
 */
export const IOSFormDateTime: React.FC<IOSFormDateTimeProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  // 格式化ISO日期为input所需格式
  const formatISODateForInput = (isoDate?: string): string => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';

      // 格式化为 yyyy-MM-ddTHH:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date for input:", error);
      return '';
    }
  };

  // 格式化日期为显示格式
  const formatDateForDisplay = (isoDate?: string): string => {
    if (!isoDate) return '选择日期和时间';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '选择日期和时间';

      // 格式化为 yyyy年MM月dd日 HH:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}年${month}月${day}日 ${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date for display:", error);
      return '选择日期和时间';
    }
  };

  return (
    <input
      type="datetime-local"
      value={formatISODateForInput(value)}
      onChange={(e) => {
        const inputValue = e.target.value;
        try {
          const newDate = inputValue ? new Date(inputValue).toISOString() : undefined;
          onChange(newDate);
        } catch (error) {
          console.error("Error parsing date:", error);
          onChange(undefined);
        }
      }}
      disabled={disabled}
      className={`w-full bg-transparent border-none focus:outline-none focus:ring-0 text-ios-body ${className} ${disabled ? 'opacity-50' : ''}`}
    />
  );
};

interface IOSFormNumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

/**
 * iOS风格的数字输入框组件
 */
export const IOSFormNumberInput: React.FC<IOSFormNumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  className = ''
}) => {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => {
        const newValue = parseFloat(e.target.value);
        if (!isNaN(newValue)) {
          onChange(newValue);
        }
      }}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      className={`w-full bg-transparent border-none focus:outline-none focus:ring-0 text-ios-body ${className} ${disabled ? 'opacity-50' : ''}`}
    />
  );
};

export const IOSFormDivider: React.FC = () => {
  return <div className="h-px bg-ios-gray5 dark:bg-ios-dark-gray4" />;
};
